/**
 * G2eGente - Onboarding/Tutorial de Primeiro Acesso
 * Guia interativo para novos usuários
 */
import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { onboardingAPI } from '../lib/api';
import { MobileTourCard } from './MobileTourCard';
import { 
  X, 
  ChevronRight, 
  ChevronLeft,
  Briefcase,
  Target,
  Search,
  BarChart3,
  User,
  FileText,
  CheckCircle,
  ArrowRight,
  Newspaper,
  GraduationCap,
  ClipboardCheck,
  Shield
} from 'lucide-react';

// Steps para Funcionário (app mobile) - `tour` aponta para [data-tour="..."]
const EMPLOYEE_STEPS = [
  { id: 1, title: 'Bem-vindo ao seu app!', description: 'Aqui você acompanha tudo da sua empresa. Veja um tour rápido — você pode tocar nos itens destacados a qualquer momento.', icon: CheckCircle, tour: null },
  { id: 2, title: 'Feed de Notícias', description: 'Comunicados e campanhas da empresa. Leia, reaja e fique por dentro das novidades.', icon: Newspaper, tour: 'feed' },
  { id: 3, title: 'Treinamentos', description: 'Acesse seus cursos online e acompanhe a validade dos seus treinamentos.', icon: GraduationCap, tour: 'treinamentos' },
  { id: 4, title: 'Avaliações', description: 'Responda autoavaliações e consulte os resultados das suas avaliações de desempenho.', icon: ClipboardCheck, tour: 'avaliacoes' },
  { id: 5, title: 'Canal de Denúncias', description: 'Relate situações de forma 100% anônima e segura.', icon: Shield, tour: 'denuncias' },
  { id: 6, title: 'Tudo pronto!', description: 'Você já conhece o essencial. Aproveite o app e fique de olho nos novos comunicados.', icon: CheckCircle, tour: null }
];

// Steps para Empresa
const COMPANY_STEPS = [
  {
    id: 1,
    title: 'Bem-vindo ao G2eGente! 🎉',
    description: 'Vamos te guiar pelos primeiros passos para encontrar os melhores talentos de forma ética.',
    icon: CheckCircle,
    target: null,
    position: 'center'
  },
  {
    id: 2,
    title: 'Publique sua primeira vaga',
    description: 'Clique em "Publicar Vaga" para criar uma oportunidade e atrair candidatos qualificados.',
    icon: Briefcase,
    target: '[data-testid="publish-job-btn"], button:has-text("Publicar Vaga")',
    position: 'bottom',
    arrow: 'up'
  },
  {
    id: 3,
    title: 'Configure o Perfil Ideal',
    description: 'Use o questionário DISC de 25 perguntas para definir o perfil comportamental ideal do candidato.',
    icon: Target,
    target: 'text=Perfil Ideal do Candidato',
    position: 'bottom',
    arrow: 'up'
  },
  {
    id: 4,
    title: 'Busque Talentos',
    description: 'Acesse "Buscar Talentos" para encontrar candidatos compatíveis com sua vaga.',
    icon: Search,
    target: 'text=Buscar Talentos',
    position: 'right',
    arrow: 'left'
  },
  {
    id: 5,
    title: 'Analise Comparativos',
    description: 'No menu "Relatório Comparativo", veja o ranking de candidatos baseado no perfil ideal.',
    icon: BarChart3,
    target: 'text=Relatório Comparativo',
    position: 'right',
    arrow: 'left'
  },
  {
    id: 6,
    title: 'Você está pronto! 🚀',
    description: 'Agora você pode publicar vagas, buscar talentos e encontrar o candidato perfeito!',
    icon: CheckCircle,
    target: null,
    position: 'center'
  }
];

// Steps para Candidato
const CANDIDATE_STEPS = [
  {
    id: 1,
    title: 'Bem-vindo ao G2eGente! 🎉',
    description: 'Sua plataforma de recrutamento ético. Vamos te ajudar a encontrar sua próxima oportunidade!',
    icon: CheckCircle,
    target: null,
    position: 'center'
  },
  {
    id: 2,
    title: 'Complete seu Perfil',
    description: 'Um perfil completo aumenta suas chances de ser encontrado por recrutadores.',
    icon: User,
    target: 'text=Meu Perfil, text=Editar Perfil',
    position: 'bottom',
    arrow: 'up'
  },
  {
    id: 3,
    title: 'Adicione seu Perfil DISC',
    description: 'Seu perfil comportamental ajuda empresas a encontrarem candidatos compatíveis com a cultura.',
    icon: Target,
    target: 'text=DISC',
    position: 'bottom',
    arrow: 'up'
  },
  {
    id: 4,
    title: 'Explore as Vagas',
    description: 'Navegue pelas oportunidades disponíveis e candidate-se às que mais combinam com você.',
    icon: Briefcase,
    target: 'text=Vagas, text=Oportunidades',
    position: 'right',
    arrow: 'left'
  },
  {
    id: 5,
    title: 'Gerencie Solicitações',
    description: 'Quando uma empresa se interessar pelo seu perfil, você receberá uma solicitação de acesso.',
    icon: FileText,
    target: 'text=Solicitações',
    position: 'right',
    arrow: 'left'
  },
  {
    id: 6,
    title: 'Boa sorte! 🍀',
    description: 'Seus dados estão protegidos e você controla quem pode acessá-los. Boa busca!',
    icon: CheckCircle,
    target: null,
    position: 'center'
  }
];

export default function OnboardingTour({ userType = 'COMPANY', onComplete }) {
  const [currentStep, setCurrentStep] = useState(0);
  const [isVisible, setIsVisible] = useState(true);

  const steps = userType === 'COMPANY' ? COMPANY_STEPS : userType === 'EMPLOYEE' ? EMPLOYEE_STEPS : CANDIDATE_STEPS;
  const step = steps[currentStep];

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      handleComplete();
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSkip = () => {
    handleComplete();
  };

  const handleComplete = () => {
    setIsVisible(false);
    localStorage.setItem(`onboarding_${userType}_completed`, 'true');
    onboardingAPI.complete(userType).catch(() => {});
    onComplete?.();
  };

  if (!isVisible) return null;

  if (userType === 'EMPLOYEE') {
    return (
      <MobileTourCard
        steps={steps}
        currentStep={currentStep}
        onNext={handleNext}
        onPrev={handlePrev}
        onSkip={handleSkip}
      />
    );
  }

  const Icon = step.icon;

  return (
    <>
      {/* Overlay */}
      <div className="fixed inset-0 bg-black/50 z-[9998]" onClick={handleSkip} />
      
      {/* Tour Card */}
      <div className={`fixed z-[9999] ${
        step.position === 'center' 
          ? 'top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2'
          : 'top-1/3 left-1/2 -translate-x-1/2'
      }`}>
        <div className="bg-white rounded-2xl shadow-2xl w-[400px] max-w-[90vw] overflow-hidden">
          {/* Header with gradient */}
          <div className="bg-gradient-to-r from-cyan-600 to-cyan-700 p-6 text-white">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                {steps.map((_, idx) => (
                  <div 
                    key={idx}
                    className={`w-2 h-2 rounded-full transition-colors ${
                      idx === currentStep ? 'bg-white' : 
                      idx < currentStep ? 'bg-cyan-300' : 'bg-cyan-400/50'
                    }`}
                  />
                ))}
              </div>
              <button 
                onClick={handleSkip}
                className="p-1 hover:bg-white/20 rounded-full transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center">
                <Icon className="w-7 h-7" />
              </div>
              <div>
                <span className="text-cyan-100 text-sm">Passo {currentStep + 1} de {steps.length}</span>
                <h2 className="text-xl font-bold">{step.title}</h2>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="p-6">
            <p className="text-slate-600 leading-relaxed mb-6">
              {step.description}
            </p>

            {/* Arrow indicator for target */}
            {step.arrow && (
              <div className={`flex items-center gap-2 p-3 bg-cyan-50 rounded-lg text-cyan-700 text-sm mb-4 ${
                step.arrow === 'up' ? 'flex-col' :
                step.arrow === 'left' ? 'flex-row' : 'flex-row-reverse'
              }`}>
                <ArrowRight className={`w-5 h-5 ${
                  step.arrow === 'up' ? '-rotate-90' :
                  step.arrow === 'left' ? 'rotate-180' : ''
                }`} />
                <span>Procure este elemento na tela</span>
              </div>
            )}

            {/* Navigation */}
            <div className="flex items-center justify-between">
              <Button
                variant="ghost"
                onClick={handlePrev}
                disabled={currentStep === 0}
                className="text-slate-500"
              >
                <ChevronLeft className="w-4 h-4 mr-1" />
                Anterior
              </Button>

              <div className="flex gap-2">
                <Button
                  variant="ghost"
                  onClick={handleSkip}
                  className="text-slate-400"
                >
                  Pular
                </Button>
                <Button
                  onClick={handleNext}
                  className="bg-cyan-600 hover:bg-cyan-700"
                >
                  {currentStep === steps.length - 1 ? 'Concluir' : 'Próximo'}
                  {currentStep < steps.length - 1 && <ChevronRight className="w-4 h-4 ml-1" />}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

// Hook para verificar se deve mostrar onboarding
export function useOnboarding(userType) {
  const [showOnboarding, setShowOnboarding] = useState(false);

  useEffect(() => {
    let timer;
    let cancelled = false;
    const schedule = () => { timer = setTimeout(() => !cancelled && setShowOnboarding(true), 1000); };
    onboardingAPI.getStatus(userType)
      .then(res => {
        if (res.data.completed) localStorage.setItem(`onboarding_${userType}_completed`, 'true');
        else schedule();
      })
      .catch(() => {
        if (!localStorage.getItem(`onboarding_${userType}_completed`)) schedule();
      });
    return () => { cancelled = true; clearTimeout(timer); };
  }, [userType]);

  return [showOnboarding, setShowOnboarding];
}
